import fire from '@store/firebase';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';
import { updateUserWallet } from '../user/updateUser';
import { CollectionPath } from '@typings/collections.enum';
import { ACCESS_TYPE } from '@typings/common/accessType';
import { ISkillItem } from '@typings/store/skill.interface';
import { IstoreHistoryItem } from '@typings/store/storeHistoryItem.interface';
import { updateUserNotification } from '../notification/update-notification';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import { getUidByCharID } from '../user/getUid';
import { ITEM_FUNCTION_TPYE } from '@typings/store/storeItemFunctionType';
import { WalletHistoryType } from '../user/addWalletHistoryByType';

const firestore = fire.firestore();

export async function getItemData({ itemID }) {
  const data: IstoreItem | any = await firestore
    .collection(`${CollectionPath.STORE}`)
    .doc(itemID)
    .get()
    .then((res) => res.data());
  return data;
}
// -----------------------------------------------------------------
//                          구매 히스토리 추가
// -----------------------------------------------------------------
export async function updateParchaseHistory({
  data,
}: {
  data: IstoreHistoryItem;
}) {
  const collectionPath = `${CollectionPath.STORE}History`;
  const ref = firestore.collection(collectionPath);

  return ref.add(data);
}

// -----------------------------------------------------------------
//                              기본 구매
// -----------------------------------------------------------------
type Props = {
  collectionPath: string;
  photocardID: string;
  data: IstoreItem | IinventoryItem | any;
  buyerID?: string;
  amount?: number;
  materialCount?: number;
  materialItemID?: string | null;
};
export async function parchaseItem({
  collectionPath,
  photocardID,
  data,
  buyerID,
  amount,
  materialCount,
  materialItemID,
}: Props) {
  // default - photocard/photocardID
  const ref = firestore.collection(
    `${collectionPath}/${photocardID}/inventory`,
  );

  // -----------------------------------------------------------------
  //                            일반 구매
  // -----------------------------------------------------------------

  // 구매자의 지갑에서 돈을 깐다.
  // 단, 선물은 buyer에게서 돈 까지 않는다.
  if (!data.giftingCharID && buyerID) {
    await updateUserWallet({
      uids: [buyerID],
      price: amount
        ? Number(data.price) * amount * -1
        : Number(data.price) * -1,
      type: WalletHistoryType.ITEM,
      memo: data.title || '',
    });
  }

  // 소비 아이템이 있을 시, 내 인벤토리에서 아이템 제거
  // ⚠️ 중요: prevItem 조회 전에 먼저 재료를 차감해야 함
  // (재료 아이템과 결과 아이템이 같을 경우 올바른 수량 계산을 위해)
  if (materialCount && materialItemID) {
    // 재료 아이템의 실제 인벤토리 데이터 가져오기
    const materialItemRef = firestore
      .collection(`${collectionPath}/${photocardID}/inventory`)
      .doc(materialItemID);
    const materialItemData = await materialItemRef
      .get()
      .then((res) => res.data());

    if (materialItemData) {
      await deleteItemsFromInventory({
        data: materialItemData as IstoreItem,
        amount: materialCount, // 재료 아이템 필요 개수만큼 차감
        collectionPath: `${collectionPath}/${photocardID}/inventory`,
      });
    }
  }

  // 하나 이상(두개부터) 구매하면, multiple 항목에 추가
  // 재료 차감 이후에 조회해야 올바른 수량 계산 가능
  const prevItem = await ref
    .doc(data.id)
    .get()
    .then((res) => res.data());

  const amountArr = amount
    ? Array.from({ length: amount }, () => data.id)
    : [data.id];

  const multipleArr =
    !prevItem || !prevItem.multiple || prevItem.multiple.length === 0
      ? [...amountArr]
      : [...prevItem.multiple, ...amountArr];

  // 구매 히스토리에 추가
  await updateParchaseHistory({
    data: {
      itemId: data.id || '',
      photocardID: photocardID || '',
      buyerID: buyerID || '',
      createdAt: new Date(),
      price: Number(data.price) || 0,
      isGift: false,
      amount: data.amount || 1,
    },
  });

  // inventory의 docID도 store itemID 와 통일
  // 랜덤박스 등 동적 생성 아이템의 경우 Store 컬렉션에 원본이 없으므로 정보를 함께 저장
  const inventoryData: any = {
    id: data.id,
    giftingCharID: '',
    originItemID: data.id,
    createdAt: new Date(),
    multiple: multipleArr,
    functionType: data.functionType ?? ITEM_FUNCTION_TPYE.DEFAULT,
    isPresent: false,
    petFunction: data.petFunction ?? null,
    giftMessage: data.giftMessage ?? '',
    // 스탯 보정 아이템용 필드 추가
    statusId: data.statusId ?? null,
    addStatusValue: data.addStatusValue ?? null,
    statusSubFunctionType: data.statusSubFunctionType ?? null,
  };

  // 랜덤박스 타입인 경우 추가 정보 저장 (Store에 원본이 없으므로)
  if (
    data.functionType === ITEM_FUNCTION_TPYE.RANDOM_BOX ||
    data.functionType === ITEM_FUNCTION_TPYE.RANDOM_BOX_POST
  ) {
    inventoryData.title = data.title;
    inventoryData.description = data.description;
    inventoryData.imgURL = data.imgURL;
    inventoryData.randomBoxData = data.randomBoxData;
  }

  return ref.doc(data.id).set(inventoryData, { merge: true });
}

// -----------------------------------------------------------------
//                      마이룸용 아이템 사용하기
// -----------------------------------------------------------------

export async function parchaseStickerItem({
  data,
  photocardID,
  buyerID,
}: {
  data: IstoreItem | IinventoryItem | any;
  photocardID: string;
  buyerID: string;
}) {
  // default - stickerBoards / boardID

  // 포토카드 데이터에서 연결된 스티커보드id를 가져와야 함 linkStickerboardId
  const linkStickerboardId = await firestore
    .collection(`${CollectionPath.PHOTOCARD}`)
    .doc(photocardID)
    .get()
    .then((res) => {
      if (res.exists) {
        const data = res.data();
        return data!.linkStickerboardId;
      }
    });

  if (!linkStickerboardId) return false;

  // stickerboard에 아이템 추가
  const stickerBoardsRef = firestore.collection(
    `${CollectionPath.STICKERBOARDS}/${linkStickerboardId}/items`,
  );
  const getMaxLength = await stickerBoardsRef.get().then((res) => res.size);
  const newData = {
    title: data.title,
    imgURL: data.imgURL,
    order: getMaxLength || 0,
    positionX: 0,
    positionY: 0,
    accessType: ACCESS_TYPE.PUBLIC,
  };

  await stickerBoardsRef.add({ ...newData }).then(async (data) => {
    await stickerBoardsRef
      .doc(data.id)
      .set({ id: data.id, ...newData }, { merge: true });
  });

  return true;
}

// -----------------------------------------------------------------
//                           인벤토리에서 삭제
// -----------------------------------------------------------------
export async function deleteItemsFromInventory({
  data,
  amount,
  collectionPath,
}: {
  data: IstoreItem;
  amount: number;
  collectionPath: string;
}) {
  // default - photocard/photocardID - 주는사람
  const ref = firestore.collection(`${collectionPath}`);

  // 나의 인벤토리에서 삭제
  if (data.multiple && data.multiple.length > 1) {
    if (!amount || amount >= data.multiple.length) {
      // multiple의 모든 항목 삭제
      await ref.doc(data.id).delete();
    } else {
      // multiple에서 뒤에서 amount만큼 삭제
      const newMultiple = data.multiple.slice(0, data.multiple.length - amount);
      await ref
        .doc(data.id)
        .set({ multiple: newMultiple, __snapshot: '' }, { merge: true });
    }
  } else {
    // multiple Arr이 하나뿐이면 내 인벤토리에서 완전히 삭제
    await ref.doc(data.id).delete();
  }
}

// -----------------------------------------------------------------
//                              선물하기
// -----------------------------------------------------------------
type PresentProps = {
  collectionPath: string;
  giftingCharID: string;
  data: IinventoryItem;
  presentCharID?: string;
  amount?: number;
  sender?: string; // 선물 알림시에 필요함
  sendToMasters?: boolean;
};
export async function presentItem({
  collectionPath, // photocard
  data,
  amount = 1,
  giftingCharID, // 선물 주는 사람
  presentCharID, // 받는 사람 : 판매할때는 TargetID없이 그냥 인벤토리 덜어내기
  sender,
  sendToMasters = true,
}: PresentProps) {
  // 나의 인벤토리에서 삭제
  await deleteItemsFromInventory({
    data: {
      ...data,
      functionType: data.functionType ?? ITEM_FUNCTION_TPYE.DEFAULT,
    },
    amount: amount ?? 1,
    collectionPath: `${collectionPath}/${giftingCharID}/inventory`,
  });

  // 아래로 '선물하기' 시 -------------------------------------------------------
  // 상대 인벤토리에 추가 photocard/photocardID
  // 선물은 buyer에게서 돈 까이지 않음
  // 선물은 겹쳐지지 않게 따로 생성되는 id로 관리
  if (!presentCharID) return;

  const targetRef = firestore.collection(
    `${collectionPath}/${presentCharID}/inventory`,
  );

  const newData = {
    createdAt: new Date(),
    multiple: [],
    isPresent: true,
    giftingCharID,
    giftMessage: data.giftMessage || '',
    __snapshot: '',
    price: 0,
  };

  // --------------------------------------
  // 알림 추가
  if (sender) {
    const receiverUserUid = await getUidByCharID({
      collectionPath,
      photocardID: presentCharID,
    });

    const alertData = {
      contents: `아이템명 : ${data.title || ''}`,
      boardAuthorUid: receiverUserUid, // 받는 사람
      authorUid: sender, //보내는 사람 sender
      boardId: presentCharID, //photocardID
      accessType: ACCESS_TYPE.PUBLIC,
    };

    updateUserNotification({
      data: alertData,
      collectionPath,
      sendToMasters,
      notiType: NOTI_TYPE.PRESENT,
      sendToBoth: true,
    });
  }
  // --------------------------------------

  return targetRef.add({
    originItemID: data.id || '',
    ...newData,
  });
}
